import { useCaptureTreeContext } from "@/components/ui/tree/capture-tree/capture-tree-context";
import { useNodeCheckbox } from "@/components/ui/tree/capture-tree/capture-tree-hooks";
import { CaptureTreeNode } from "@/components/ui/tree/capture-tree/capture-tree-node";
import { useCaptureTreeSelectionStore } from "@/components/ui/tree/capture-tree/capture-tree-selection-store";
import { TreeNodeProps } from "@/components/ui/tree/tree-node";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { FaroCheckbox } from "@faro-lotv/flat-ui";
import { useCallback, useMemo, type JSX } from "react";
import { NodeApi } from "react-arborist";

type CreateReportTreeNodeProps = TreeNodeProps<TreeData>;

/** @returns the component that represents each node of the capture tree, when the Create Report tool is active */
export function CreateReportTreeNode({
  node,
  ...rest
}: CreateReportTreeNodeProps): JSX.Element {
  const onNodeCheckbox = useNodeCheckbox();
  const onChecked = useCallback(
    () => onNodeCheckbox(node),
    [node, onNodeCheckbox],
  );

  const { isChecked, isIndeterminate } = useNodeCheckboxState(node);

  const onCheckedChanged = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (ev) => {
      onChecked();
      ev.preventDefault();
      ev.stopPropagation();
    },
    [onChecked],
  );

  return (
    <CaptureTreeNode
      node={node}
      extraElements={
        <FaroCheckbox
          checked={isChecked}
          onClick={onCheckedChanged}
          indeterminate={isIndeterminate}
          controlSx={{ m: 0, mr: 1 }}
        />
      }
      {...rest}
    />
  );
}

type CheckBoxState = {
  /** If true, the checkbox is selected */
  isChecked: boolean;
  /** If true, not all children of the node have been selected */
  isIndeterminate: boolean;
};

/**
 * @returns The selection state of a checkbox of a node in the create report tree
 * @param node The node of the tree to check
 */
export function useNodeCheckboxState(node: NodeApi<TreeData>): CheckBoxState {
  const { selectedIds } = useCaptureTreeSelectionStore();
  const { isVisible } = useCaptureTreeContext();

  const isChecked = useMemo(
    () => selectedIds.has(node.id) || (node.isRoot && selectedIds.size > 0),
    [node.id, node.isRoot, selectedIds],
  );

  const isIndeterminate = useMemo(() => {
    if (!isChecked) return false;

    const stack = [...(node.children ?? [])];
    while (stack.length > 0) {
      const child = stack.pop();
      if (!child) continue;

      if (isVisible(child.id) && !selectedIds.has(child.id)) {
        return true;
      }

      stack.push(...(child.children ?? []));
    }

    return false;
  }, [isChecked, isVisible, node.children, selectedIds]);

  return useMemo(
    () => ({ isChecked, isIndeterminate }),
    [isChecked, isIndeterminate],
  );
}
